import React, { useEffect } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { Paper } from '@material-ui/core'
import { useDispatch, useSelector } from 'react-redux'
import { EditOutlined as EditIcon, DeleteOutlineOutlined as DeleteIcon } from '@material-ui/icons'
import { useConfirm } from 'material-ui-confirm'
import { fetchSessionById, resetSessionStore } from 'store/session'
import talkSlice, { deleteTalkById, fetchTalks } from 'store/talk'
import { fetchEventById, resetEventStore } from 'store/event'
import venueSlice, { fetchVenues } from 'store/venue'
import speakerSlice, { getSpeakers } from 'store/speaker'
import { renderCol } from 'utils/renderColumns'
import ViewHeader from 'components/ViewHeader'
import ResourceNotFound from 'components/ResourceNotFound'
import ActionBar from 'components/ActionBar'
import getTableOptions from 'utils/getTableOptions'
import BasicTable from 'components/BasicTable'
import Info from 'components/Info'
import { convertTimestampToLocalString } from 'utils/times'
import Wrapper from 'components/Wrapper'
import { caseInsensitiveSort } from 'utils'
import CopyButton from 'components/CopyButton'
import Tooltip from '@material-ui/core/Tooltip'

const app_base_url = window.location.origin

const TalkListing = () => {
  const dispatch = useDispatch()
  const { eventId, sessionId } = useParams()

  const confirm = useConfirm()
  const history = useHistory()
  const location = useLocation()
  const { talks, inProgress: talkInProgress } = useSelector((state) => state.talk)
  const { event, inProgress: eventInProgress } = useSelector((state) => state.event)
  const { session, inProgress: sessionInProgress } = useSelector((state) => state.session)
  const { user } = useSelector((state) => state.auth)

  const inProgress = talkInProgress || eventInProgress || sessionInProgress
  const { venues } = useSelector((state) => state.venue)
  const { speakers } = useSelector((state) => state.speaker)
  const { conference } = useSelector((state) => state.conference)

  useEffect(() => {
    if (eventId) {
      dispatch(fetchEventById(eventId))
    }
    if (sessionId) {
      dispatch(fetchSessionById(sessionId))
    }
    dispatch(talkSlice.actions.setProgress(true))
    const unsub = fetchTalks(eventId, sessionId, (data) => {
      dispatch(talkSlice.actions.setTalks(data))
      dispatch(talkSlice.actions.setProgress(false))
    })
    const unsubVenues = fetchVenues(user?.conferenceId, (venuesData) => {
      dispatch(venueSlice.actions.setVenues(venuesData))
    })
    return () => {
      unsub && unsub()
      unsubVenues && unsubVenues()
      dispatch(resetSessionStore())
      dispatch(resetEventStore())
      dispatch(talkSlice.actions.setTalks([]))
    }
  }, [eventId, sessionId, user?.conferenceId])

  useEffect(() => {
    const unsub = getSpeakers(user?.conferenceId, (data) => {
      dispatch(speakerSlice.actions.setSpeakers(caseInsensitiveSort(data, 'fullName')))
    })
    return () => unsub && unsub()
  }, [dispatch, user?.conferenceId])

  const tableHead = (event) => {
    return [
      {
        name: 'Sr #',
        options: {
          customBodyRender: (value) => <div style={{ width: '40px' }}>{value}</div>
        }
      },
      `${event?.isPosterSession ? 'Poster #' : 'Abstract #'}`,
      'Title',
      {
        name: 'Venue',
        options: {
          customBodyRender: (value) => renderCol(value)
        }
      },
      'Speakers',
      {
        name: 'Start Time',
        options: {
          customBodyRender: (value) => (
            <div style={{ minWidth: '130px' }}>
              {convertTimestampToLocalString(value, conference?.timezone, conference?.is24hrFormat)}
            </div>
          )
        }
      },
      {
        name: 'End Time',
        options: {
          customBodyRender: (value) => (
            <div style={{ minWidth: '130px' }}>
              {convertTimestampToLocalString(value, conference?.timezone, conference?.is24hrFormat)}
            </div>
          )
        }
      },
      {
        name: 'Has Poll',
        options: {
          filter: false,
          sort: false,
          customBodyRender: (value) => <div style={{ width: '30px' }}>{value}</div>
        }
      },
      {
        name: 'Actions',
        options: {
          filter: false,
          sort: false,
          customBodyRender: (value) => renderCol(value),
          customHeadLabelRender: (meta) => renderCol(meta.label)
        }
      }
    ]
  }

  const goBack = () => {
    dispatch(resetSessionStore)
    dispatch(resetEventStore())
    history.goBack()
  }

  const formatTableData = (talks) =>
    talks?.map((talk) => {
      const talkVenue = venues?.find((type) => type.id === talk?.venueId)
      let talkSpeakers = talk?.speakers
        ?.slice(0, 3)
        ?.map((speaker) => {
          const talkSpeaker = speakers?.find((s) => s.id === speaker)
          return talkSpeaker?.fullName ? talkSpeaker.fullName : null
        })
        .join(', ')
      if (talk?.speakers?.length > 2) talkSpeakers += ' and others'
      let abs_pos_no = ''
      if (event?.isPosterSession && talk?.isAbstract && talk?.posterNumber) {
        abs_pos_no = `${conference?.posterPrefix} - ${talk.posterNumber}`
      } else if (talk?.isAbstract && talk?.abstractNumber) {
        abs_pos_no = `${conference?.abstractPrefix} - ${talk.abstractNumber}`
      }
      return [
        talk?.srNo,
        abs_pos_no,
        talk.title,
        talkVenue?.venueText,
        talkSpeakers,
        talk.startDate,
        talk.endDate,
        talk?.pollId ? 'Yes' : 'No',
        <div style={{ width: 120, display: 'flex', alignItems: 'center', gap: '8px' }}>
          {talk?.pollId && (
            <CopyButton
              value={`${app_base_url}/polls/${talk?.pollId}`}
              type="icon"
              tooltip="Copy poll link"
            />
          )}
          <Tooltip title="Edit Talk">
            <EditIcon
              color="action"
              style={{ cursor: 'pointer' }}
              onClick={() => {
                history.push(`${location.pathname}/edit/${talk.id}`)
              }}
            />
          </Tooltip>
          <Tooltip title="Delete Talk">
            <DeleteIcon
              color="action"
              style={{ cursor: 'pointer' }}
              onClick={async () => {
                try {
                  await confirm({
                    title: 'Delete Talk',
                    description: 'Are you sure you want to delete this Talk ?'
                  })
                  dispatch(deleteTalkById(talk.id))
                } catch {
                  // do nothing
                }
              }}
            />
          </Tooltip>
        </div>
      ]
    })

  const options = getTableOptions()
  return (
    <>
      <ViewHeader title={event?.eventType === 'haveTalks' ? 'Event Talks' : 'Sessions Talks'}>
        <div style={{ marginRight: 10 }}>
          <ActionBar actionText="Back" onAction={() => goBack()} />
        </div>
        <ActionBar
          actionText="New Talk"
          onAction={() => history.push(`${location.pathname}/new`)}
          searchPlaceholder="Search Talks"
        />
      </ViewHeader>

      <Wrapper inProgress={inProgress}>
        {event && eventId === event?.id && !sessionId && (
          <Info
            eventTitle={event.title}
            eventStartDate={event ? event.startDate : null}
            pinCode={conference?.havePolls && event?.pinCode}
            eventEndDate={event ? event.endDate : null}
            timezone={conference?.timezone}
            is24hrFormat={conference?.is24hrFormat}
          />
        )}

        {event && eventId === event.id && sessionId && session?.id === sessionId && (
          <Info
            eventTitle={event.title}
            eventStartDate={event ? event.startDate : null}
            eventEndDate={event ? event.endDate : null}
            pinCode={conference?.havePolls && session?.pinCode}
            sessionTitle={session?.title}
            sessionStartDate={session?.startDate}
            sessionEndDate={session?.endDate}
            timezone={conference?.timezone}
            is24hrFormat={conference?.is24hrFormat}
          />
        )}

        {talks && talks.length ? (
          <Paper variant="outlined" style={{ marginTop: 20 }}>
            <BasicTable
              data={formatTableData(talks)}
              columns={tableHead(event)}
              options={options}
            />
          </Paper>
        ) : (
          <ResourceNotFound message="No talk available" />
        )}
      </Wrapper>
    </>
  )
}

export default TalkListing

// const useStyles = makeStyles({
//   iconBtn: {
//     padding: '4px'
//   }
// })
